import React from "react";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
    Checkbox,
} from "@material-tailwind/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import FormikInput from "../form/formik-input";

export default function RestartSurveyDialog({
    showDialog,
    handleClose,
    survey,
    surveyId,
    handleRestartDialogSubmit,
}) {
    const handleCloseDialog = () => {
        handleClose();
    };

    const validationSchema = Yup.object({
        start_date: Yup.string()
            .when('startNow', {
                is: false,
                then: (schema) => schema
                    .required("Start date is required")
                    .test(
                        "future-date",
                        "Start date must be in future",
                        (value) => {
                            return dayjs(value).isAfter(dayjs().subtract(1, 'minute'));
                        }
                    ),
                otherwise: (schema) => schema.strip(),
            }),
        end_date: Yup.string()
            .required("End date is required")
            .test(
                "future-date",
                "End date must be in future",
                (value) => {
                    return dayjs(value).isAfter(dayjs().subtract(1, 'minute'));
                }
            )
            .test(
                "after-start-date",
                "End date must be after start date",
                function(value) {
                    const { startNow } = this.parent;
                    // Skip check when starting now - backend handles start_date
                    if (startNow) return true;
                    const { start_date } = this.parent;
                    return dayjs(value).isAfter(dayjs(start_date));
                }
            ),
        startNow: Yup.boolean(),
    });

    return (
        <Dialog
            open={showDialog}
            handler={handleCloseDialog}
            className="dialog"
        >
            <DialogHeader className="dialog__header">
                Restart Survey
            </DialogHeader>
            <Formik
                initialValues={{
                    start_date: survey?.start_date
                        ? dayjs.utc(survey.start_date).local().format("YYYY-MM-DDTHH:mm")
                        : "",
                    end_date: survey?.end_date
                        ? dayjs.utc(survey.end_date).local().format("YYYY-MM-DDTHH:mm")
                        : "",
                    startNow: false
                }}
                onSubmit={handleRestartDialogSubmit}
                validationSchema={validationSchema}
            >
                {({ values, errors, touched, setFieldValue, isValid }) => (
                    <Form>
                        <DialogBody className="dialog__body">
                            <Typography className="mb-4">
                                To restart this survey, both start and end dates must be in future.
                                Please update the dates below.
                            </Typography>

                            {/* Start Date Section */}
                            <div className="mb-4">
                                <FormikInput
                                    name="start_date"
                                    type="datetime-local"
                                    label="Start Date"
                                    className="bg-white"
                                    disabled={values.startNow}
                                    autoFocus={false}
                                    tabIndex="-1"
                                />
                                {touched.start_date && errors.start_date && (
                                    <Typography color="red" className="text-sm mt-1">
                                        {errors.start_date}
                                    </Typography>
                                )}
                            </div>

                            {/* Start Now Checkbox */}
                            <div className="mb-4 flex items-center">
                                <Checkbox
                                    checked={values.startNow}
                                    label="Start survey now"
                                    labelProps={{
                                        className: "text-primary font-body",
                                    }}
                                    className="bg-white"
                                    onChange={() => {
                                        if (values.startNow) {
                                            setFieldValue("startNow", false);
                                        } else {
                                            const now = dayjs().format("YYYY-MM-DDTHH:mm");
                                            setFieldValue("start_date", now);
                                            setFieldValue("startNow", true);
                                        }
                                    }}
                                />
                            </div>

                            {/* End Date Section */}
                            <div className="mb-4">
                                <FormikInput
                                    name="end_date"
                                    type="datetime-local"
                                    label="End Date"
                                    className="bg-white"
                                    autoFocus={false}
                                    tabIndex="-1"
                                />
                                {touched.end_date && errors.end_date && (
                                    <Typography color="red" className="text-sm mt-1">
                                        {errors.end_date}
                                    </Typography>
                                )}
                            </div>

                            <Typography className="text-sm text-gray-600">
                                Restarting will set survey to Published status and allow
                                respondents to use existing survey invitation links once active.
                            </Typography>
                        </DialogBody>
                        <DialogFooter className="dialog__footer">
                            <Button
                                type="button"
                                className="btn btn--secondary"
                                variant="filled"
                                onClick={() => handleCloseDialog()}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="btn btn--primary ml-2"
                                variant="filled"
                                disabled={!isValid}
                            >
                                Restart
                            </Button>
                        </DialogFooter>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}
